import { useEffect, useRef, useState } from 'react';
import GoogleAuthService from '../../services/googleAuth';
import LoadingSpinner from './LoadingSpinner';

interface GoogleSignInButtonProps {
  onSuccess: (credential: string) => void;
  onError: (error: Error) => void;
  disabled?: boolean;
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  className?: string;
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onSuccess,
  onError,
  disabled = false,
  text = 'continue_with',
  theme = 'outline',
  size = 'large',
  className = '',
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [error, setError] = useState<string>('');
  const [isDevelopment, setIsDevelopment] = useState(false);

  useEffect(() => {
    const initializeGoogleAuth = async () => {
      try {
        setIsLoading(true);
        setError('');

        // Check if Google OAuth is configured
        if (!GoogleAuthService.isConfigured()) {
          setError('Google OAuth is not configured. Please set up Google Cloud Console credentials.');
          setIsDevelopment(true);
          return;
        }

        setIsConfigured(true);

        // Initialize Google Auth Service
        await GoogleAuthService.initialize();

        // Delay to ensure DOM is ready
        setTimeout(() => {
          if (buttonRef.current && !disabled) {
            GoogleAuthService.renderButton(
              buttonRef.current,
              {
                theme,
                size,
                text,
                shape: 'rectangular',
                logo_alignment: 'left',
              },
              (credential: string) => {
                setIsLoading(false);
                onSuccess(credential);
              },
              (error: Error) => {
                setIsLoading(false);
                setError(error.message);
                onError(error);
              }
            );
          }
        }, 100); // 100ms delay
      } catch (err) {
        setIsLoading(false);
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Google Sign-In';
        setError(errorMessage);
        setIsDevelopment(true);
        console.error('Google Auth initialization error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeGoogleAuth();
  }, [onSuccess, onError, disabled, text, theme, size]);

  // Development mode mock sign-in (SECURITY: Only in development)
  const handleDevSignIn = () => {
    // SECURITY: Only allow mock sign-in in development mode
    if (isDevelopment && import.meta.env.MODE === 'development') {
      // Create a mock JWT token for development
      const mockCredential = 'mock.jwt.token.for.development';
      console.log('Development mode: Using mock Google credential');
      onSuccess(mockCredential);
    } else {
      console.error('SECURITY: Mock sign-in attempted in non-development environment');
      onError(new Error('Mock authentication is not allowed in this environment'));
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className={`w-full bg-white border border-gray-300 text-gray-700 font-medium py-3 px-4 rounded-lg flex items-center justify-center ${className}`}>
        <LoadingSpinner size="sm" color="text-gray-600" />
        <span className="ml-2">Loading Google Sign-In...</span>
      </div>
    );
  }

  // Show development mode button (SECURITY: Only in development)
  if (isDevelopment && error && import.meta.env.MODE === 'development') {
    return (
      <div className={`w-full ${className}`}>
        <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg mb-3">
          <div className="text-sm text-amber-700">
            <div className="font-medium mb-1">🔧 Development Mode</div>
            <div className="text-xs mb-2">Google OAuth not configured. Using mock authentication.</div>
            <div className="text-xs text-blue-600">
              To fix: Set up Google Cloud Console credentials and add http://localhost:3000 to authorized origins
            </div>
          </div>
        </div>
        <button
          onClick={handleDevSignIn}
          disabled={disabled}
          className="w-full bg-white border border-gray-300 text-gray-700 font-medium py-3 px-4 rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google (Development)
        </button>
      </div>
    );
  }

  // Show error state
  if (error && !isDevelopment) {
    return (
      <div className={`w-full bg-gray-100 border border-gray-300 text-gray-500 font-medium py-3 px-4 rounded-lg flex items-center justify-center ${className}`}>
        <div className="text-center">
          <div className="text-sm text-red-600 mb-1">⚠️ Google Sign-In Unavailable</div>
          <div className="text-xs text-gray-500">{error}</div>
          <div className="text-xs text-blue-600 mt-2">
            For development: Add http://localhost:3000 to Google OAuth authorized origins
          </div>
        </div>
      </div>
    );
  }

  // Show not configured state
  if (!isConfigured && !isDevelopment) {
    return (
      <div className={`w-full bg-amber-50 border border-amber-200 text-amber-700 font-medium py-3 px-4 rounded-lg flex items-center justify-center ${className}`}>
        <div className="text-center">
          <div className="text-sm font-medium mb-1">🔧 Setup Required</div>
          <div className="text-xs">Google OAuth credentials need to be configured</div>
        </div>
      </div>
    );
  }

  // Show disabled state
  if (disabled) {
    return (
      <div className={`w-full bg-gray-100 border border-gray-300 text-gray-400 font-medium py-3 px-4 rounded-lg flex items-center justify-center cursor-not-allowed ${className}`}>
        <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continue with Google (Disabled)
      </div>
    );
  }

  // Render the actual Google button container
  return (
    <div className={`w-full ${className}`}>
      <div 
        ref={buttonRef} 
        className="flex justify-center"
        style={{ minHeight: '44px' }} // Ensure consistent height
      />
    </div>
  );
};

export default GoogleSignInButton; 